import {
  defaultLocale,
  getLangFileFromLocale,
  getLocale,
  Locale,
} from '@repo/utils'
import { Hydrate, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import {
  EnvironmentTypeContextProvider,
  EnvironmentTypeEnum,
  LogsContextProvider,
  TravelTypeContextProvider,
} from '@wome/ui'
import type { AppContext, AppProps } from 'next/app'
import { useRouter } from 'next/router'
import React from 'react'
import { IntlProvider } from 'react-intl'
import { ToastContainer } from 'react-toastify'
import { Toaster } from 'sonner'
import { LanguageSwitchProvider } from 'ui/src/components/layout/header/language-switch/language-switch.provider'

import '@styles/main.scss'

import { isDev } from '@/lib/constants'

import { queryClient } from '@/api/clients'
import AuthProvider from '@/providers/auth/auth.provider'
import { HistoryProvider } from '@/providers/HistoryProvider'
import { HouseProvider } from '@/providers/HouseProvider'
import { PwaContextProvider } from '@/providers/PwaContextProvider'

function MyApp({ Component, pageProps }: AppProps) {
  const { locale } = pageProps
  const router = useRouter()

  return (
    <IntlProvider
      messages={pageProps.intlMessages}
      locale={locale}
      defaultLocale={defaultLocale}
    >
      <LanguageSwitchProvider
        pathname={router.pathname}
        searchParams={router.asPath.split('?')[1] || ''}
        onLocaleChange={() => Promise.resolve()}
        resolveTranslatedPath={({ asPath, targetLocale }) =>
          `/${targetLocale}${asPath}`
        }
      >
        <QueryClientProvider client={queryClient}>
          <Hydrate state={pageProps.dehydratedState}>
            <AuthProvider
              isAuthenticateTokenExpired={
                pageProps?.isAuthenticateTokenExpired as boolean
              }
            >
              <EnvironmentTypeContextProvider value={EnvironmentTypeEnum.OWNER}>
                <PwaContextProvider>
                  <LogsContextProvider>
                    <HistoryProvider>
                      <TravelTypeContextProvider>
                        <HouseProvider>
                          {isDev && (
                            <ReactQueryDevtools initialIsOpen={false} />
                          )}
                          <Component {...pageProps} />
                          <Toaster />
                          <ToastContainer />
                        </HouseProvider>
                      </TravelTypeContextProvider>
                    </HistoryProvider>
                  </LogsContextProvider>
                </PwaContextProvider>
              </EnvironmentTypeContextProvider>
            </AuthProvider>
          </Hydrate>
        </QueryClientProvider>
      </LanguageSwitchProvider>
    </IntlProvider>
  )
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const { ctx, Component } = appContext
  const locale = getLocale(ctx.locale)
  const intlMessages = await getMessages(locale)

  let pageProps = {
    logout: false,
    intlMessages,
    locale,
  }

  if (Component.getInitialProps) {
    pageProps = {
      ...(await Component.getInitialProps(ctx)),
      logout: false,
      intlMessages,
      locale,
    }
  }

  return { pageProps }
}

export default MyApp

async function getMessages(locale: Locale) {
  try {
    const messages = await import(
      `../src/lang-compiled/${getLangFileFromLocale(locale)}.json`
    )
    return messages.default
  } catch (error) {
    throw new Error(
      'Could not load compiled language files. Please run "npm run i18n:compile" first',
    )
  }
}
